<script setup lang="ts">
import { computed, onMounted, ref, watch, markRaw, onBeforeUnmount } from "vue";
import {
  Xmark,
  Menu,
  HomeSimple,
  Building,
  Wallet,
  User,
  Group,
  Page,
  StatsUpSquare,
  DollarCircle,
  Puzzle,
  UserPlus,
} from "@iconoir/vue";
import Logo from "~/components/Logo.vue";
import TopBar from "~/components/TopBar.vue";
import { AdminPermission, type AdminPermissionValue } from "~~/schemas/admin-role";

interface MenuItem {
  icon: any;
  label: string;
  to?: string;
  perm?: AdminPermissionValue | AdminPermissionValue[];
  children?: MenuItem[];
}

interface MenuSection {
  label?: string;
  items: MenuItem[];
}

const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();
const { can } = useAccess();

const { user } = storeToRefs(authStore);

const firstLoading = computed(() => !user.value);
const expandedMenus = ref<string[]>([]);
const isCollapsed = ref(false);
const isMenuOpen = ref(false);
const isResizing = ref(false);
let resizeTimer: any;

const handleResize = () => {
  isResizing.value = true;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    isResizing.value = false;
  }, 200);
};

const toggleMobileMenu = () => {
  isCollapsed.value = false;
  isMenuOpen.value = !isMenuOpen.value;
};

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
  if (isCollapsed.value) {
    expandedMenus.value = [];
  }
};

const rawMenuSections = computed<MenuSection[]>(() => [
  {
    items: [
      { icon: markRaw(HomeSimple), label: t("navigation.dashboard"), to: "/", perm: AdminPermission.AnalyticsRead },
      {
        icon: markRaw(StatsUpSquare),
        label: t("navigation.analytics"),
        children: [
          { icon: null, label: t("navigation.analyticsWorkspaces"), to: "/analytics/workspaces", perm: AdminPermission.AnalyticsRead },
          { icon: null, label: t("navigation.analyticsUsage"), to: "/analytics/usage", perm: AdminPermission.AnalyticsRead },
          { icon: null, label: t("navigation.financeReports"), to: "/finance/reports", perm: AdminPermission.AnalyticsRead },
        ],
      },
    ],
  },
  {
    label: t("navigation.operation"),
    items: [
      { icon: markRaw(Building), label: t("navigation.workspace"), to: "/workspace", perm: AdminPermission.WorkspacesRead },
      { icon: markRaw(Wallet), label: t("navigation.plans"), to: "/workspace-plan", perm: AdminPermission.WorkspacePlansRead },
      { icon: markRaw(Puzzle), label: t("navigation.apps"), to: "/apps", perm: AdminPermission.AppsRead },
      { icon: markRaw(UserPlus), label: t("lead.title"), to: "/leads", perm: AdminPermission.LeadsRead },
      {
        icon: markRaw(DollarCircle),
        label: t("navigation.finance"),
        children: [
          { icon: null, label: t("navigation.workspaceOrders"), to: "/workspace-orders", perm: AdminPermission.WorkspaceOrdersRead },
          { icon: null, label: t("navigation.financeCosts"), to: "/finance/costs", perm: AdminPermission.CostsRead },
        ],
      },
      {
        icon: markRaw(Page),
        label: t("navigation.library"),
        children: [
          { icon: null, label: t("navigation.siteTemplate"), to: "/site-template", perm: AdminPermission.SiteTemplatesRead },
          { icon: null, label: t("navigation.globalAsset"), to: "/global-asset" },
        ],
      },
    ],
  },
  {
    label: t("navigation.settings"),
    items: [
      { icon: markRaw(User), label: t("navigation.adminUser"), to: "/administrators", perm: AdminPermission.AdminUsersRead },
      { icon: markRaw(Group), label: t("adminRole.title"), to: "/roles", perm: AdminPermission.AdminRolesRead },
    ],
  },
]);

function filterByPerm(item: MenuItem): MenuItem | null {
  if (item.children) {
    const visibleChildren = item.children
      .map(filterByPerm)
      .filter((c): c is MenuItem => c !== null);
    if (visibleChildren.length === 0) return null;
    return { ...item, children: visibleChildren };
  }
  if (item.perm && !can(item.perm)) return null;
  return item;
}

const menuSections = computed<MenuSection[]>(() =>
  rawMenuSections.value
    .map((section) => ({
      ...section,
      items: section.items
        .map(filterByPerm)
        .filter((i): i is MenuItem => i !== null),
    }))
    .filter((section) => section.items.length > 0),
);

function isMenuExpanded(item: MenuItem) {
  return expandedMenus.value.includes(item.label);
}

function getRouteMatchScore(to?: string) {
  if (!to) return -1;
  if (route.path === to) return 10000 + to.length;
  if (to !== "/" && route.path.startsWith(to + "/")) return to.length;
  return -1;
}

const activeTo = computed(() => {
  let bestTo: string | undefined;
  let bestScore = -1;

  for (const section of menuSections.value) {
    for (const item of section.items) {
      const score = getRouteMatchScore(item.to);
      if (score > bestScore) {
        bestScore = score;
        bestTo = item.to;
      }

      if (item.children) {
        for (const child of item.children) {
          const childScore = getRouteMatchScore(child.to);
          if (childScore > bestScore) {
            bestScore = childScore;
            bestTo = child.to;
          }
        }
      }
    }
  }

  return bestTo;
});

const { title: pageTitle } = usePageTitle();

function isSubItemActive(child: MenuItem) {
  return !!child.to && child.to === activeTo.value;
}

function isMenuActive(item: MenuItem) {
  if (isSubItemActive(item)) return true;
  if (item.children) {
    return item.children.some((child) => isSubItemActive(child));
  }
  return false;
}

function handleMenuClick(item: MenuItem) {
  if (item.children) {
    if (isMenuExpanded(item)) {
      expandedMenus.value = expandedMenus.value.filter(
        (label) => label !== item.label,
      );
    } else {
      if (isCollapsed.value) {
        isCollapsed.value = false;
        setTimeout(() => {
          expandedMenus.value.push(item.label);
        }, 150);
      } else {
        expandedMenus.value.push(item.label);
      }
    }
  }
}

function updateExpandedMenus() {
  for (const section of menuSections.value) {
    for (const item of section.items) {
      if (item.children && isMenuActive(item)) {
        if (!expandedMenus.value.includes(item.label)) {
          expandedMenus.value.push(item.label);
        }
      }
    }
  }
}

watch(
  () => route.path,
  () => {
    updateExpandedMenus();
    isMenuOpen.value = false;
  },
);

const activeMenuItem = computed(() => {
  if (!activeTo.value) return null;

  for (const section of menuSections.value) {
    for (const item of section.items) {
      if (item.children) {
        const activeChild = item.children.find(
          (child) => child.to === activeTo.value,
        );
        if (activeChild) return activeChild;
      }
      if (item.to === activeTo.value) return item;
    }
  }

  return null;
});

useHead({
  title: computed(() => {
    const menuLabel = activeMenuItem.value?.label;
    return menuLabel ? `Artech Admin - ${menuLabel}` : "Artech Admin";
  }),
});

onMounted(() => {
  window.addEventListener("resize", handleResize);
  updateExpandedMenus();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  if (import.meta.client) {
    document.body.style.overflow = "";
  }
});
</script>

<template>
  <div class="default-layout">
    <aside class="sidebar" :class="{ 'is-collapsed': isCollapsed, 'no-transition': isResizing }">
      <template v-if="user">

        <div class="sidebar__toggle" @click="toggleMobileMenu">
          <Menu v-if="!isMenuOpen"></Menu>
          <Xmark v-else />
        </div>

        <div class="sidebar__logo">
          <Logo :compact="isCollapsed" />
        </div>

        <span class="sidebar__mobile-title text-body-primary">{{ pageTitle }}</span>

        <div class="sidebar__mobile-avatar">
          <UserMenu />
        </div>

        <div class="sidebar__body" v-if="!firstLoading" :class="{ 'is-open': isMenuOpen, 'no-transition': isResizing }">
          <nav class="sidebar__nav">
            <template v-for="(section, sIndex) in menuSections" :key="sIndex">
              <div class="sidebar__section" :class="{ 'is-collapsed': isCollapsed }">
                <div v-if="section.label" class="sidebar__section-label text-body-small-secondary">
                  {{ section.label }}
                </div>
                <template v-for="(item, iIndex) in section.items" :key="iIndex">
                  <MenuItem v-if="item.children" :icon="item.icon" :label="item.label"
                    :selected="isMenuActive(item) && !isMenuExpanded(item)" :expanded="isMenuExpanded(item)"
                    @click="handleMenuClick(item)">
                  <template #submenu>
                    <MenuItem v-for="(child, cIndex) in item.children" :key="cIndex" :label="child.label" :to="child.to"
                      :selected="isSubItemActive(child)" is-sub-item>
                    </MenuItem>
                  </template>
                  </MenuItem>

                  <MenuItem v-else :icon="item.icon" :label="item.label" :to="item.to" :selected="isMenuActive(item)">
                  </MenuItem>
                </template>
              </div>
            </template>
          </nav>
        </div>
      </template>
    </aside>

    <main class="default-layout__main">
      <TopBar :collapsed="isCollapsed" @toggle-sidebar="toggleSidebar" />
      <div class="default-layout__content">
        <slot v-if="!firstLoading"></slot>
      </div>
    </main>
  </div>
</template>

<style scoped lang="scss">
.default-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: token("color-bg-canvas");
  position: relative;

  @include down("md") {
    flex-direction: column;
    gap: 0;
  }

  &__main {
    flex: 1;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: token("padding-small") token("padding-medium");

    @include up("md") {
      padding: token("padding-large") token("layout-margin");
    }
  }
}

.sidebar {
  position: relative;
  width: 240px;
  height: 100%;
  background: token("color-bg-container-glass");
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  z-index: 100;
  padding: token("padding-xsmall");
  gap: token("gap-xsmall");
  transition: width token("motion-duration-standard") token("motion-ease-standard");
  border-right: 1px solid token("color-border-neutral-alpha-light-default");

  @include down("md") {
    width: 100%;
    flex-direction: row;
    height: auto;
    align-items: center;
    gap: token("gap-2xsmall");
    justify-content: space-between;
  }

  &__logo {
    display: flex;
    align-items: center;
    padding: token("padding-small") token("padding-medium");
    height: 64px;
    flex-shrink: 0;

    @include down("md") {
      display: none;
    }
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: token("dm-36");
    height: token("dm-36");
    flex-shrink: 0;

    @include up("md") {
      display: none;
    }
  }

  &.is-collapsed {
    width: 64px;

    @include down("md") {
      width: 100%;
    }

    @include up("md") {
      :deep() {
        .menu-item {
          min-width: 36px;
          height: 36px;
          display: flex;

          &__label,
          &__right,
          &__arrow {
            display: none;
          }

          &__content {
            justify-content: center;
          }
        }
      }

      .sidebar__logo {
        justify-content: center;
        padding: 0;
      }
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 0 token("dm-6");

    @include down("md") {
      gap: token("gap-large");
      position: absolute;
      left: 0;
      top: 100%;
      width: 100%;
      height: calc(100vh - 60px);
      background: token("color-bg-surface");
      z-index: 999;
      display: flex;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transform: translateY(-8px);
      transition:
        opacity token("motion-duration-standard") token("motion-ease-standard"),
        transform token("motion-duration-standard") token("motion-ease-standard"),
        visibility token("motion-duration-standard") token("motion-ease-standard");

      &.is-open {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateY(0);
      }
    }
  }

  &__nav {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: relative;
    z-index: 2;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: token("gap-2xsmall");
    padding: token("padding-xsmall") 0;
    border-bottom: 1px solid token("color-border-neutral-alpha-light-default");

    &:last-child {
      border-bottom: none;
    }

    &-label {
      padding: token("padding-xsmall") token("padding-medium");
      padding-left: token("padding-2xsmall");
      color: token("color-content-neutral-light");
      overflow: hidden;
      height: 32px;
      transition: height token("motion-duration-standard") token("motion-ease-standard");
    }

    &.is-collapsed {
      .sidebar__section-label {
        display: none;
        height: 0px !important;
      }
    }
  }

  &__mobile-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: token("color-content-neutral-strong");

    @include up("md") {
      display: none;
    }
  }

  &__mobile-avatar {
    @include up("md") {
      display: none;
    }
  }
}
</style>
